import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 p-4" data-testid="terms-page">
      <div className="max-w-3xl mx-auto py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600 hover:text-slate-900"
          data-testid="terms-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="shadow-2xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl sm:text-3xl font-bold">Terms of Service</CardTitle>
            <p className="text-sm text-slate-500 mt-2">Last updated: July 2026</p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none px-6 sm:px-10 pb-8 text-sm sm:text-base text-slate-700 leading-relaxed space-y-6">

            <section>
              <h3 className="text-lg font-semibold text-slate-900">1. Acceptance of Terms</h3>
              <p>
                By accessing or using the TaskPriority Prime application and website (taskpriority.net), 
                you agree to be bound by these Terms of Service. If you do not agree to these terms, 
                please do not use the application.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">2. Description of Service</h3>
              <p>
                TaskPriority Prime is a personal task management application that allows users to create, 
                organize, and track daily tasks using an ABC prioritization system. The service includes 
                task management, journal/notes features, and customizable themes.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">3. User Accounts</h3>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials 
                and for all activities that occur under your account. You agree to provide accurate 
                and complete information when creating your account and to update your information 
                as necessary.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">4. Acceptable Use</h3>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1 text-slate-600">
                <li>Use the service for any unlawful purpose or in violation of any applicable laws</li>
                <li>Attempt to gain unauthorized access to any part of the service or its systems</li>
                <li>Interfere with or disrupt the service or servers connected to the service</li>
                <li>Use the service to transmit any malicious code, viruses, or harmful content</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">5. User Content</h3>
              <p>
                You retain ownership of any content (tasks, notes, journal entries) you create 
                within the application. We do not claim ownership of your content. However, 
                you grant us a limited license to store and process your content solely to 
                provide the service to you.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">6. Data Retention</h3>
              <p>
                Task data is retained for up to 366 days. Journal entries are stored locally 
                in your browser. You may delete your tasks at any time through the application. 
                For account deletion requests, please contact us directly.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">7. Service Availability</h3>
              <p>
                We strive to keep TaskPriority Prime available at all times, but we do not guarantee 
                uninterrupted access. The service may be temporarily unavailable due to maintenance, 
                updates, or circumstances beyond our control. We are not liable for any loss or 
                inconvenience caused by service downtime.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">8. Disclaimer of Warranties</h3>
              <p>
                The service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without 
                warranties of any kind, either express or implied. We do not warrant that the 
                service will be error-free, secure, or meet your specific requirements.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">9. Limitation of Liability</h3>
              <p>
                To the fullest extent permitted by law, TaskPriority Prime and its owner shall not 
                be liable for any indirect, incidental, special, consequential, or punitive damages, 
                including but not limited to loss of data, profits, or goodwill, arising from your 
                use of or inability to use the service.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">10. Changes to Terms</h3>
              <p>
                We reserve the right to modify these terms at any time. Changes will be effective 
                upon posting to the application. Your continued use of the service after changes 
                are posted constitutes your acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">11. Termination</h3>
              <p>
                We reserve the right to suspend or terminate your access to the service at our 
                discretion, without notice, for conduct that we believe violates these terms or 
                is harmful to other users or the service.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900">12. Contact</h3>
              <p>
                If you have questions about these Terms of Service, please contact us at{' '}
                <a href="mailto:rpfarrah@gmail.com" className="text-blue-600 hover:underline">
                  rpfarrah@gmail.com
                </a>.
              </p>
            </section>

            <div className="border-t pt-4 mt-6 text-center text-xs text-slate-400">
              &copy; 2026 Robert Farrah. All rights reserved.
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;
